/**
 * Pushed updates and rollbacks (docs/plans/2026-10-update-management.md; the workbench's
 * docs/plans/2026-10-pushed-updates.md). An administrator orders one machine, or every machine in a
 * ring, to check or install now, or to roll back to a release the platform holds. The order rides
 * on the machine's heartbeats until it reports the version; the pending line says where it stands.
 */

import { compareVersions, type FleetDevice, type ReleaseManifest, type ReleasesOverview, type UpdateOrderCreate, type UpdateOrderProduct, type UpdateOrderRecord } from '@aioe/shared';
import { time } from '@aioe/shared/wording/dateTime';
import { Button, Input, TextField } from '@heroui/react';
import { useMemo, useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { ErrorNote, ListRow, StatusMark } from './ui.tsx';
import { FormSheet } from './ui/FormSheet.tsx';
import { OptionSelect } from './updatesFields.tsx';
import { timeAgo } from '../hooks.ts';

const PRODUCT_TEXT: Record<UpdateOrderProduct, string> = { workbench: 'AI Workbench', node: 'Node', mesh: 'Mesh agent' };

/** What a pending or recent order says on a fleet row: "ordered 0.47.1 at 10:02, reported 0.47.2". */
export function orderLine(o: UpdateOrderRecord): string {
  const what = o.action === 'check' ? 'check ordered' : `${o.allowDowngrade ? 'roll back to' : 'install'} ${o.version ?? 'the offered release'} ordered`;
  const when = time(o.requestedAt);
  switch (o.state) {
    case 'pending':
      return `${PRODUCT_TEXT[o.product]}: ${what} at ${when}${o.reportedVersion ? `, reported ${o.reportedVersion}` : ''}`;
    case 'done':
      return `${PRODUCT_TEXT[o.product]}: ${what} at ${when}, done`;
    case 'expired':
      return `${PRODUCT_TEXT[o.product]}: ${what} at ${when}, not carried out`;
    default:
      return `${PRODUCT_TEXT[o.product]}: ${what} at ${when}, cancelled`;
  }
}

export function OrderMark({ o }: { o: UpdateOrderRecord }) {
  return <StatusMark state={o.state === 'pending' ? 'working' : o.state === 'done' ? 'online' : o.state === 'expired' ? 'error' : 'disabled'} label={orderLine(o)} wrap />;
}

/** The releases the platform holds for a product, newest first, withdrawn ones left out. */
function held(overview: ReleasesOverview | undefined, product: UpdateOrderProduct): ReleaseManifest[] {
  const p = overview?.products.find((x) => x.product === product);
  return (p?.releases ?? []).filter((r) => !r.yanked).sort((a, b) => compareVersions(b.version, a.version));
}

type Choice = 'check' | 'install' | `rollback:${string}`;

/**
 * The control itself: pick what to do and say why. `onSend` places the order (one machine or a ring);
 * `running` is the version the target runs now, so a rollback offers only older releases.
 */
export function OrderControl({ products, overview, running, onSend, compact }: { products: UpdateOrderProduct[]; overview: ReleasesOverview | undefined; running?: Partial<Record<UpdateOrderProduct, string>>; onSend: (body: UpdateOrderCreate) => Promise<unknown>; compact?: boolean }) {
  const [product, setProduct] = useState<UpdateOrderProduct>(products[0]!);
  const [choice, setChoice] = useState<Choice>('install');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [sent, setSent] = useState<string>();
  const releases = useMemo(() => held(overview, product), [overview, product]);
  const current = running?.[product];
  const older = current ? releases.filter((r) => compareVersions(r.version, current) < 0) : releases;
  const send = async () => {
    setBusy(true);
    setError(undefined);
    setSent(undefined);
    try {
      const body: UpdateOrderCreate = choice === 'check' ? { product, action: 'check', allowDowngrade: false, ...(reason ? { reason } : {}) } : choice === 'install' ? { product, action: 'install', allowDowngrade: false, ...(reason ? { reason } : {}) } : { product, action: 'install', version: choice.slice('rollback:'.length), allowDowngrade: true, ...(reason ? { reason } : {}) };
      await onSend(body);
      setSent(choice === 'check' ? 'Check ordered.' : choice === 'install' ? 'Install ordered.' : `Rollback to ${choice.slice('rollback:'.length)} ordered.`);
      setReason('');
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`grid gap-2 ${compact ? '' : 'sm:grid-cols-2'}`}>
      {products.length > 1 ? (
        <OptionSelect
          ariaLabel="What to update"
          className="w-full sm:w-40"
          value={product}
          options={products.map((p) => ({ id: p, label: PRODUCT_TEXT[p] }))}
          onChange={(p) => {
            setProduct(p);
            setChoice('install');
          }}
        />
      ) : (
        <span className="self-center text-sm">{PRODUCT_TEXT[product]}</span>
      )}
      <OptionSelect<Choice>
        ariaLabel="Action"
        value={choice}
        options={[{ id: 'install', label: 'Install the offered release' }, { id: 'check', label: 'Check for updates' }, ...older.map((r) => ({ id: `rollback:${r.version}` as Choice, label: `Roll back to ${r.version}` }))]}
        onChange={setChoice}
      />
      <div className={`flex items-center gap-2 ${compact ? '' : 'sm:col-span-2'}`}>
        <TextField aria-label="Reason" className="min-w-0 flex-1" value={reason} onChange={setReason}>
          <Input placeholder="Reason, shown to the person (optional)" />
        </TextField>
        <Button size="sm" isPending={busy} onPress={() => void send()}>
          Send
        </Button>
      </div>
      {error ? <div className={compact ? '' : 'sm:col-span-2'}><ErrorNote error={error} /></div> : null}
      {sent ? <p className={`text-xs text-muted ${compact ? '' : 'sm:col-span-2'}`}>{sent} The machine is told on its next heartbeat, within a minute.</p> : null}
    </div>
  );
}

/**
 * A machine's updates, as rows for a list group: what it runs, each open order (with Cancel) and the
 * last few, then one row whose button opens the order form in a sheet (install, check, or roll back).
 * The caller supplies the group (Layout.tsx ListGroup), so the rows share its card and hairlines.
 */
export function DeviceUpdates({ device, orders, overview, reload }: { device: FleetDevice; orders: UpdateOrderRecord[]; overview: ReleasesOverview | undefined; reload: () => void }) {
  const [ordering, setOrdering] = useState(false);
  const mine = orders.filter((o) => o.deviceId === device.deviceId);
  const open = mine.filter((o) => o.state === 'pending');
  const recent = mine.filter((o) => o.state !== 'pending').slice(0, 3);
  const products: UpdateOrderProduct[] = device.kind === 'node' ? ['node', 'mesh'] : ['workbench', 'mesh'];
  const running: Partial<Record<UpdateOrderProduct, string>> = { [device.kind === 'node' ? 'node' : 'workbench']: device.appVersion, ...(device.components?.mesh ? { mesh: device.components.mesh } : {}) };
  const name = device.kind === 'node' ? 'Node' : 'AI Workbench';
  return (
    <>
      <ListRow title={name} description={`Seen ${timeAgo(device.lastSeenAt)}`} value={device.appVersion ? <span className="mono text-foreground">{device.appVersion}</span> : 'Not reported'} />
      <ListRow title="Mesh agent" value={device.components?.mesh ? <span className="mono text-foreground">{device.components.mesh}</span> : 'Not reported'} />
      {open.map((o) => (
        <ListRow
          key={o.id}
          title="Waiting for the machine"
          description={orderLine(o)}
          status={<StatusMark state="working" label="Ordered" />}
          compactControl
          control={
            <Button size="sm" variant="ghost" onPress={() => void routes.cancelUpdateOrder(o.id).then(reload)}>
              Cancel
            </Button>
          }
        />
      ))}
      {recent.length ? (
        <ListRow title="Recent orders">
          <div className="grid gap-1">
            {recent.map((o) => (
              <OrderMark key={o.id} o={o} />
            ))}
          </div>
        </ListRow>
      ) : null}
      <ListRow
        title="Update or roll back"
        description="Install the offered release, check for updates, or roll back to a release the platform holds."
        control={
          <Button size="sm" variant="secondary" onPress={() => setOrdering(true)}>
            Send an order
          </Button>
        }
      />
      <FormSheet isOpen={ordering} width="md" title={`Update ${device.hostname}`} description="The machine is told on its next heartbeat, within a minute, and the order rides on its heartbeats until it reports the version." onClose={() => setOrdering(false)}>
        <OrderControl
          compact
          products={products}
          overview={overview}
          running={running}
          onSend={async (body) => {
            await routes.orderDeviceUpdate(device.deviceId, body);
            reload();
          }}
        />
      </FormSheet>
    </>
  );
}
